<template>
  <v-card variant="outlined" class="recurrence-card">
    <v-card-text class="px-4 pt-4 pb-3">

      <!-- Send once / Recurring toggle -->
      <div class="text-subtitle-2 font-weight-bold mb-3">Schedule</div>
      <v-radio-group v-model="scheduleType" inline hide-details class="mb-4">
        <v-radio label="Send once" :value="ONCE" />
        <v-radio label="Recurring" :value="RECURRING" />
      </v-radio-group>

      <!-- ── Send Once ────────────────────────────────── -->
      <div v-if="scheduleType === ONCE">
        <input
          type="datetime-local"
          v-model="scheduledAt"
          :min="minDateTime"
          class="datetime-input"
        />
        <div v-if="description" class="mt-3 preview-text text-caption text-medium-emphasis">
          <v-icon size="x-small" class="me-1">mdi-information-outline</v-icon>{{ description }}
        </div>
      </div>

      <!-- ── Recurring ───────────────────────────────── -->
      <div v-else>

        <!-- Repeat every -->
        <div class="d-flex align-center gap-2 mb-5">
          <span class="text-body-2 flex-shrink-0">Repeat every</span>
          <v-text-field
            v-model.number="interval"
            type="number"
            min="1"
            max="99"
            density="compact"
            variant="outlined"
            hide-details
            style="width: 74px"
          />
          <v-select
            v-model="unit"
            :items="unitOptions"
            item-title="label"
            item-value="value"
            density="compact"
            variant="outlined"
            hide-details
            style="width: 120px"
          />
        </div>

        <!-- Repeat on — weekly day picker -->
        <div v-if="unit === 'week'" class="mb-5">
          <div class="text-body-2 mb-2">Repeat on</div>
          <div class="d-flex gap-2">
            <button
              v-for="(day, idx) in dayLabels"
              :key="idx"
              type="button"
              :class="['day-btn', { 'day-btn--active': selectedDays.includes(idx) }]"
              @click="toggleDay(idx)"
            >{{ day }}</button>
          </div>
        </div>

        <!-- Day of month — monthly -->
        <div v-if="unit === 'month'" class="d-flex align-center gap-2 mb-5">
          <span class="text-body-2 flex-shrink-0">Day of month</span>
          <v-text-field
            v-model.number="dayOfMonth"
            type="number"
            min="1"
            max="31"
            density="compact"
            variant="outlined"
            hide-details
            style="width: 80px"
          />
        </div>

        <!-- Time of day -->
        <div class="d-flex align-center gap-3 mb-5">
          <span class="text-body-2 flex-shrink-0">Time</span>
          <input type="time" v-model="runTime" class="time-input" />
        </div>

        <!-- Ends -->
        <div class="text-body-2 font-weight-bold mb-3">Ends</div>
        <v-radio-group v-model="endType" hide-details class="ends-group">

          <v-radio value="never" label="Never" />

          <v-radio value="on">
            <template #label>
              <span class="me-3">On</span>
              <input
                v-if="endType === 'on'"
                type="date"
                v-model="endDate"
                :min="minDate"
                class="date-input"
                @click.stop
              />
              <span v-else class="text-caption text-medium-emphasis">
                {{ endDate ? fmtDate(endDate) : 'pick a date' }}
              </span>
            </template>
          </v-radio>

          <v-radio value="after">
            <template #label>
              <span class="me-2">After</span>
              <template v-if="endType === 'after'">
                <input
                  type="number"
                  v-model.number="occurrences"
                  min="1"
                  class="count-input"
                  @click.stop
                />
                <span class="ms-1 text-body-2">occurrences</span>
              </template>
              <span v-else class="text-caption text-medium-emphasis">
                {{ occurrences }} occurrences
              </span>
            </template>
          </v-radio>

        </v-radio-group>

        <!-- Preview -->
        <div v-if="description" class="mt-4 preview-text text-caption text-medium-emphasis">
          <v-icon size="x-small" class="me-1">mdi-information-outline</v-icon>{{ description }}
        </div>

      </div>

    </v-card-text>
  </v-card>
</template>

<script>
const ONCE      = 1
const RECURRING = 2

export default {
  name: 'RecurrenceEditor',
  props: {
    modelValue: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['update:modelValue'],

  data() {
    const now = new Date()
    now.setMinutes(now.getMinutes() + 30)
    return {
      ONCE,
      RECURRING,

      // Schedule type
      scheduleType: ONCE,
      scheduledAt:  now.toISOString().slice(0, 16),

      // Recurring fields
      interval:     1,
      unit:         'week',
      selectedDays: [1],
      dayOfMonth:   1,
      runTime:      '09:00',
      endType:      'never',
      endDate:      '',
      occurrences:  1,

      dayLabels:   ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
      unitOptions: [
        { label: 'day',   value: 'day'   },
        { label: 'week',  value: 'week'  },
        { label: 'month', value: 'month' }
      ]
    }
  },

  computed: {
    minDateTime() {
      return new Date().toISOString().slice(0, 16)
    },
    minDate() {
      return new Date().toISOString().slice(0, 10)
    },

    cronExpression() {
      if (this.scheduleType === ONCE) return null
      const [h, m] = this.runTime.split(':').map(Number)
      const hour = isNaN(h) ? 9 : h
      const min  = isNaN(m) ? 0 : m
      const N    = Math.max(1, this.interval || 1)

      if (this.unit === 'day') {
        return N === 1 ? `${min} ${hour} * * *` : `${min} ${hour} */${N} * *`
      }
      if (this.unit === 'week') {
        const dow = this.selectedDays.length
          ? [...this.selectedDays].sort((a, b) => a - b).join(',')
          : '*'
        return `${min} ${hour} * * ${dow}`
      }
      if (this.unit === 'month') {
        const dom = Math.min(31, Math.max(1, this.dayOfMonth || 1))
        return N === 1 ? `${min} ${hour} ${dom} * *` : `${min} ${hour} ${dom} */${N} *`
      }
      return `${min} ${hour} * * *`
    },

    description() {
      if (this.scheduleType === ONCE) {
        if (!this.scheduledAt) return ''
        return `Sends once on ${new Date(this.scheduledAt).toLocaleString()}`
      }
      const dayNames = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday']
      const [h, m]   = this.runTime.split(':').map(Number)
      const timeStr  = `${String(isNaN(h) ? 9 : h).padStart(2,'0')}:${String(isNaN(m) ? 0 : m).padStart(2,'0')}`
      const N        = Math.max(1, this.interval || 1)

      let base = ''
      if (this.unit === 'day') {
        base = N === 1 ? 'Every day' : `Every ${N} days`
      } else if (this.unit === 'week') {
        const names = this.selectedDays.length
          ? this.selectedDays.map(d => dayNames[d]).join(', ')
          : 'every day'
        base = N === 1 ? `Every week on ${names}` : `Every ${N} weeks on ${names}`
      } else if (this.unit === 'month') {
        const dom = Math.min(31, Math.max(1, this.dayOfMonth || 1))
        base = N === 1 ? `Every month on day ${dom}` : `Every ${N} months on day ${dom}`
      }
      base += ` at ${timeStr}`
      if (this.endType === 'on' && this.endDate) {
        base += ` · ends ${this.fmtDate(this.endDate)}`
      } else if (this.endType === 'after') {
        base += ` · ${this.occurrences} time${this.occurrences !== 1 ? 's' : ''}`
      }
      return base
    }
  },

  watch: {
    scheduleType() { this.emitValue() },
    scheduledAt()  { this.emitValue() },
    cronExpression() { this.emitValue() },
    endType()      { this.emitValue() },
    endDate()      { this.emitValue() },
    occurrences()  { this.emitValue() }
  },

  methods: {
    toggleDay(idx) {
      const pos = this.selectedDays.indexOf(idx)
      if (pos === -1) {
        this.selectedDays.push(idx)
      } else if (this.selectedDays.length > 1) {
        this.selectedDays.splice(pos, 1)
      }
      this.emitValue()
    },

    emitValue() {
      this.$emit('update:modelValue', {
        scheduleType:   this.scheduleType,
        scheduledAt:    this.scheduleType === ONCE ? this.scheduledAt : null,
        cronExpression: this.cronExpression,
        endType:        this.endType,
        endDate:        this.endType === 'on'    ? this.endDate     : null,
        maxOccurrences: this.endType === 'after' ? this.occurrences : null,
        description:    this.description
      })
    },

    fmtDate(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    },

    parseCronIntoState(cron) {
      const parts = (cron || '').trim().split(/\s+/)
      if (parts.length !== 5) return
      const [minStr, hourStr, domStr, monthStr, dowStr] = parts
      const h = parseInt(hourStr)
      const m = parseInt(minStr)
      if (!isNaN(h) && !isNaN(m)) {
        this.runTime = `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`
      }
      if (dowStr !== '*') {
        this.unit = 'week'
        this.selectedDays = dowStr.split(',').map(Number).filter(n => !isNaN(n))
        if (!this.selectedDays.length) this.selectedDays = [1]
      } else if (domStr !== '*') {
        this.unit = 'month'
        this.dayOfMonth = parseInt(domStr) || 1
        if (monthStr.startsWith('*/')) this.interval = parseInt(monthStr.slice(2)) || 1
      } else {
        this.unit = 'day'
        if (domStr.startsWith('*/')) this.interval = parseInt(domStr.slice(2)) || 1
      }
    }
  },

  created() {
    const mv = this.modelValue
    if (mv) {
      if (mv.scheduleType) this.scheduleType = mv.scheduleType
      if (mv.scheduledAt)  this.scheduledAt  = mv.scheduledAt
      if (mv.cronExpression) {
        this.scheduleType = RECURRING
        this.parseCronIntoState(mv.cronExpression)
      }
      if (mv.endType)        this.endType    = mv.endType
      if (mv.endDate) {
        const d = new Date(typeof mv.endDate === 'number' ? mv.endDate : mv.endDate)
        this.endDate = d.toISOString().slice(0, 10)
      }
      if (mv.maxOccurrences) this.occurrences = mv.maxOccurrences
    }
    this.emitValue()
  }
}
</script>

<style scoped>
.recurrence-card {
  max-width: 480px;
}

.day-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid #9e9e9e;
  background: transparent;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  color: #555;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.day-btn:hover       { border-color: #1976d2; color: #1976d2; }
.day-btn--active     { background: #1976d2; border-color: #1976d2; color: #fff; }

.datetime-input,
.time-input,
.date-input {
  height: 36px;
  padding: 0 10px;
  border: 1px solid #9e9e9e;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  background: #fff;
}
.datetime-input       { width: 220px; }
.datetime-input:focus,
.time-input:focus,
.date-input:focus     { border-color: #1976d2; }

.count-input {
  width: 64px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid #9e9e9e;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  background: #fff;
}
.count-input:focus { border-color: #1976d2; }

.ends-group  { gap: 4px; }
.preview-text { border-left: 3px solid #e0e0e0; padding-left: 8px; }
</style>
